// Essay Template Matching Service
// Maps scholarship requirements to relevant essay templates
// Scales automatically — new scholarships get matches based on requirement text analysis

import { essayTemplates, type EssayTemplate } from '@/data/essayTemplates';
import type { Scholarship, Requirement } from '@/types';

// ── Keyword → Category mapping ──────────────────────────────────────────
// When a scholarship requirement mentions these keywords, suggest these template categories.
// This is the core of the scalable architecture: new scholarships automatically
// get template suggestions based on their requirement text, no hardcoding needed.

type EssayCategory = EssayTemplate['category'];

interface KeywordRule {
  keywords: string[];
  categories: EssayCategory[];
  /** Higher weight = stronger match (default 1) */
  weight?: number;
}

const KEYWORD_RULES: KeywordRule[] = [
  // Explicit essay type mentions
  { keywords: ['leadership essay', 'leadership experience'], categories: ['leadership'], weight: 3 },
  { keywords: ['community essay', 'community involvement', 'community impact', 'service essay', 'community service'], categories: ['community-impact'], weight: 3 },
  { keywords: ['financial need', 'financial hardship', 'financial circumstances'], categories: ['financial-need'], weight: 3 },
  { keywords: ['career goals', 'career essay', 'future goals', 'professional goals'], categories: ['career-goals'], weight: 3 },
  { keywords: ['adversity essay', 'overcoming challenges', 'overcoming adversity', 'obstacle'], categories: ['overcoming-adversity'], weight: 3 },
  { keywords: ['diversity essay', 'cultural background', 'identity essay'], categories: ['diversity'], weight: 3 },
  { keywords: ['stem essay', 'innovation essay', 'research essay', 'scientific'], categories: ['stem-innovation'], weight: 3 },
  { keywords: ['creative arts', 'artistic', 'art portfolio essay', 'creative writing'], categories: ['creative-arts'], weight: 3 },
  { keywords: ['why this scholarship', 'why you deserve', 'why should you receive'], categories: ['why-this-scholarship'], weight: 3 },
  { keywords: ['extracurricular', 'activities essay'], categories: ['extracurricular'], weight: 3 },
  { keywords: ['mentorship', 'mentor essay'], categories: ['mentorship'], weight: 3 },
  { keywords: ['failure', 'lesson learned', 'mistake'], categories: ['failure-lesson'], weight: 3 },
  { keywords: ['ethical', 'moral dilemma'], categories: ['ethical-dilemma'], weight: 3 },
  { keywords: ['future vision', 'vision for the future', 'change the world'], categories: ['future-vision'], weight: 3 },
  { keywords: ['gratitude', 'thankful'], categories: ['gratitude'], weight: 3 },
  { keywords: ['teamwork', 'collaboration', 'team experience'], categories: ['teamwork'], weight: 3 },
  { keywords: ['passion project', 'passion'], categories: ['passion-project'], weight: 2 },
  { keywords: ['social justice', 'equity', 'activism'], categories: ['social-justice'], weight: 3 },
  { keywords: ['family heritage', 'family background', 'first generation', 'first-gen'], categories: ['family-heritage'], weight: 2 },

  // Generic essay/personal statement — suggest the most universally useful categories
  { keywords: ['personal statement', 'personal essay'], categories: ['overcoming-adversity', 'career-goals', 'leadership', 'why-this-scholarship'], weight: 1 },
  { keywords: ['essay', 'essays', 'short answer'], categories: ['why-this-scholarship', 'leadership', 'community-impact', 'career-goals'], weight: 0.5 },
];

// ── Scholarship name → Category mapping ─────────────────────────────────
// Supplements keyword matching by checking if the scholarship name appears
// in any template's commonScholarships list.

function getTemplatesByScholarshipName(scholarshipName: string): EssayTemplate[] {
  const nameLower = scholarshipName.toLowerCase();
  return essayTemplates.filter(t =>
    t.commonScholarships.some(s => nameLower.includes(s.toLowerCase()) || s.toLowerCase().includes(nameLower))
  );
}

// ── Core matching function ──────────────────────────────────────────────

interface TemplateMatch {
  template: EssayTemplate;
  score: number;
  /** Why this template was suggested */
  reason: string;
}

/**
 * Get essay template suggestions for a scholarship.
 *
 * Matching strategy (in priority order):
 * 1. Direct name match — scholarship name appears in template's commonScholarships
 * 2. Requirement keyword match — requirement text maps to template categories
 * 3. Category fallback — scholarship category maps to likely essay types
 *
 * This scales automatically: new scholarships get suggestions based on their
 * requirement text without any manual mapping needed.
 */
export function getEssayTemplatesForScholarship(scholarship: Scholarship): TemplateMatch[] {
  const scored = new Map<string, TemplateMatch>();

  // 1. Direct name match (highest priority)
  const nameMatches = getTemplatesByScholarshipName(scholarship.name);
  for (const template of nameMatches) {
    scored.set(template.id, {
      template,
      score: 10,
      reason: `Commonly used for ${scholarship.name}`,
    });
  }

  // 2. Requirement keyword matching
  const essayRequirements = scholarship.requirements.filter(r => isEssayRequirement(r));

  for (const req of essayRequirements) {
    const reqLower = req.item.toLowerCase();

    for (const rule of KEYWORD_RULES) {
      const matched = rule.keywords.some(kw => reqLower.includes(kw));
      if (!matched) continue;

      const weight = rule.weight ?? 1;
      for (const category of rule.categories) {
        const templates = essayTemplates.filter(t => t.category === category);
        for (const template of templates) {
          const existing = scored.get(template.id);
          const newScore = (existing?.score ?? 0) + (5 * weight);
          scored.set(template.id, {
            template,
            score: newScore,
            reason: existing?.reason || `Matches "${req.item}" requirement`,
          });
        }
      }
    }
  }

  // 3. Scholarship category fallback — if we still have few matches
  if (scored.size < 2 && essayRequirements.length > 0) {
    const categoryMapping: Record<string, EssayCategory[]> = {
      'STEM': ['stem-innovation', 'career-goals'],
      'Community Service': ['community-impact', 'leadership'],
      'Leadership': ['leadership', 'teamwork'],
      'Arts': ['creative-arts', 'passion-project'],
      'Need-Based': ['financial-need', 'overcoming-adversity'],
      'Merit-Based': ['leadership', 'career-goals', 'why-this-scholarship'],
      'First-Generation': ['family-heritage', 'overcoming-adversity'],
      'Minority': ['diversity', 'community-impact'],
      'Athletic': ['leadership', 'teamwork'],
    };

    const fallbackCategories = categoryMapping[scholarship.category] ?? ['why-this-scholarship', 'career-goals'];
    for (const category of fallbackCategories) {
      const templates = essayTemplates.filter(t => t.category === category);
      for (const template of templates) {
        if (!scored.has(template.id)) {
          scored.set(template.id, {
            template,
            score: 2,
            reason: `Recommended for ${scholarship.category} scholarships`,
          });
        }
      }
    }
  }

  // Sort by score descending, return top matches
  return Array.from(scored.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}

// ── Helpers ─────────────────────────────────────────────────────────────

/** Check if a requirement is essay-related */
export function isEssayRequirement(req: Requirement): boolean {
  const item = req.item.toLowerCase();
  return (
    item.includes('essay') ||
    item.includes('personal statement') ||
    item.includes('short answer') ||
    item.includes('writing sample') ||
    item.includes('written response')
  );
}

/** Check if a requirement is rec-letter-related */
export function isRecLetterRequirement(req: Requirement): boolean {
  const item = req.item.toLowerCase();
  return (
    item.includes('recommendation') ||
    item.includes('letter of rec') ||
    item.includes('reference letter') ||
    item.includes('references')
  );
}

/** Parse how many essays a requirement asks for (e.g. "2 Essays" → 2) */
export function parseEssayCount(req: Requirement): number {
  const match = req.item.match(/(\d+)\s*(essay|personal statement)/i);
  return match ? parseInt(match[1], 10) : 1;
}

/** Parse how many rec letters a requirement asks for */
export function parseRecLetterCount(req: Requirement): number {
  const match = req.item.match(/(\d+)\s*(letter|recommendation|reference)/i);
  return match ? parseInt(match[1], 10) : 1;
}

/** Extract word count guidance from requirement text (e.g. "500 words" → 500) */
export function parseWordCount(req: Requirement): number | null {
  const match = req.item.match(/(\d+)\s*words?/i);
  return match ? parseInt(match[1], 10) : null;
}

/** Quick check: does this scholarship require any essay? */
export function scholarshipRequiresEssay(scholarship: Scholarship): boolean {
  return scholarship.requirements.some(r => isEssayRequirement(r));
}

/** Quick check: does this scholarship require rec letters? */
export function scholarshipRequiresRecLetter(scholarship: Scholarship): boolean {
  return scholarship.requirements.some(r => isRecLetterRequirement(r));
}
